import { createDraftSafeSelector, createSelector } from '@reduxjs/toolkit';
import { fetchAllDashboardsRequest, TimeRange, upsertDashboardRequest } from 'actions/dashboard';
import defaultDashboardConfig from 'constants/defaultDashboardConfig';
import emptyDefaultDashboardConfig from 'constants/emptyDefaultDashboardConfig';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { GlobalState } from 'reducers';
import {
  Dashboard,
  DashboardConfig,
  DashboardFilter,
  RouteParams,
  Selector,
  User,
  UUID,
} from 'types';
import {
  addDatesToFiltersSearch,
  DataNode,
  getAllFieldsValuesWithLabels,
  lookAllForField,
  navParamsFromTree,
  Tree,
  TreeFieldsValuesWithLabels,
  ValueNode,
} from 'utils/parserTree';
import { getNavParamsByResource, getParams } from './nav';

export const getCustomerDashboardConfig: Selector<DashboardConfig & { key: string }, [UUID]> =
  createSelector(
    [
      (state: GlobalState): GlobalState['auth'] => state.auth,
      (_state: GlobalState, activeUuid: string): string => activeUuid,
    ],
    (auth, activeUuid): DashboardConfig & { key: string } => {
      if (activeUuid && auth.user.customer?.dashboards) {
        const dashboard = auth.user.customer?.dashboards[activeUuid];

        if (dashboard && dashboard.config) {
          return {
            key: activeUuid,
            ...(dashboard.config as DashboardConfig),
          };
        }
      }
      // use to develop from default
      // return { key: 'global_default', ...defaultDashboardConfig };

      function getDefaultFilters(filter: DashboardFilter): DashboardFilter {
        const defaultCustomerFilter = auth.user.customer?.config.dashboard_config?.filters?.find(
          (fil) => fil.dimension === filter.dimension
        );

        return {
          ...filter,
          default_value:
            defaultCustomerFilter?.default_value !== null
              ? defaultCustomerFilter?.default_value
              : undefined,
        };
      }

      if (auth.user.customer?.config.dashboard_config != null) {
        if (auth.user.customer?.config.dashboard_config?.filters) {
          return {
            key: 'customer_default',
            ...auth.user.customer?.config.dashboard_config,
            filters: [
              ...(auth.user.customer?.config.dashboard_config?.filters as DashboardFilter[]).map(
                (filter) => getDefaultFilters(filter)
              ),
            ],
          };
        }
        return {
          key: 'customer_default',
          ...auth.user.customer?.config.dashboard_config,
        };
      }

      return { key: 'global_empty_default', ...emptyDefaultDashboardConfig };
    },
    // consider a selector factory
    { memoizeOptions: { maxSize: 10 } }
  );

export const getWidgetFiltersById =
  (id: string, activeUuid: string | null): Selector<DashboardFilter[] | undefined> =>
  ({ auth }): DashboardFilter[] | undefined => {
    if (auth.user.customer?.dashboards && activeUuid) {
      const dashboard = auth.user.customer?.dashboards[activeUuid];
      const widgetItem = dashboard.config?.widgets.find(({ type, widget }, currentIndex) => {
        const widgetId =
          type != null
            ? `${widget}--${type}--${currentIndex}--${activeUuid}`
            : `${widget}--${currentIndex}--${activeUuid}`;

        return widgetId === id;
      });
      return widgetItem?.filters;
    }
    let widgetItem = defaultDashboardConfig.widgets.find(({ widget, type }, index) => {
      const widgetId =
        type != null
          ? `${widget}--${type}--${index}--global_default`
          : `${widget}--${index}--global_default`;
      return id === widgetId;
    });
    if (widgetItem) {
      return widgetItem?.filters;
    }

    widgetItem = auth.user.customer?.config.dashboard_config?.widgets.find(
      ({ widget, type }, index) => {
        const widgetId =
          type != null
            ? `${widget}--${type}--${index}--customer_default`
            : `${widget}--${index}--customer_default`;
        return id === widgetId;
      }
    );
    if (widgetItem) {
      return widgetItem?.filters;
    }
    return undefined;
  };

export const getActiveUuid: Selector<string | null> = ({ dashboard }) => dashboard.activeUuid;

export const getLoadedDashboards: Selector<Dashboard[]> = ({ dashboard }) =>
  Object.values(dashboard.dashboards);

export const getDashboardsLoading: Selector<boolean> = (state) =>
  state.dashboard.loading.includes(fetchAllDashboardsRequest.toString());

export const getUpsertDashboardLoading: Selector<boolean> = (state) =>
  state.dashboard.loading.includes(upsertDashboardRequest.toString());

export const getDashboardTimeRange: Selector<TimeRange> = (state) => state.dashboard.timeRange;

export const getUser = (state: GlobalState): User => state.auth.user;

type ReturnDashboarState = (
  state: GlobalState
) => { key: string; user: User; name: string } & DashboardConfig;

export const getDashboardState = (activeUuid?: string): ReturnDashboarState =>
  createDraftSafeSelector([getUser], (user) => {
    if (activeUuid && user.customer?.dashboards) {
      const dashboard = user.customer?.dashboards[activeUuid];

      if (dashboard && dashboard.config) {
        return {
          key: activeUuid,
          user,
          name: dashboard.name,
          ...(dashboard.config as DashboardConfig),
        };
      }
    }

    // use to develop from default
    // return { key: 'global_default', user, ...defaultDashboardConfig };

    function getDefaultFilters(filter: DashboardFilter): DashboardFilter {
      const defaultCustomerFilter = user.customer?.config.dashboard_config?.filters?.find(
        (fil) => fil.dimension === filter.dimension
      );

      return {
        ...filter,
        default_value:
          defaultCustomerFilter?.default_value !== null
            ? defaultCustomerFilter?.default_value
            : undefined,
      };
    }

    if (user.customer?.config.dashboard_config != null) {
      if (user.customer?.config.dashboard_config?.filters) {
        return {
          key: 'customer_default',
          user,
          name: 'default',
          ...user.customer?.config.dashboard_config,
          filters: [
            ...(user.customer?.config.dashboard_config?.filters as DashboardFilter[]).map(
              (filter) => getDefaultFilters(filter)
            ),
          ],
        };
      }
      return {
        key: 'customer_default',
        user,
        name: 'default',
        ...user.customer?.config.dashboard_config,
      };
    }

    return {
      key: 'global_empty_default',
      user,
      name: 'global-default',
      ...emptyDefaultDashboardConfig,
    };
  });

type ReturnSidebarState = (state: GlobalState) => {
  customerDomain: string;
  loadedDashboards: Dashboard[];
  params: RouteParams;
};

export const getSidebarState = (resource?: string): ReturnSidebarState =>
  createDraftSafeSelector([getParams, getUser], (p, user) => {
    const response: RouteParams = {};
    const params = { ...p };

    Object.entries(params).forEach(([key, value]) => {
      const [paramResource, ...field] = key.split('__');
      if (paramResource === resource) {
        response[field.join('__')] = value;
      }
    });

    return {
      customerDomain: user.customer?.domain ?? 'r',
      loadedDashboards: Object.values(user.customer?.dashboards ?? {}) || [],
      params: response,
    };
  });

export const getExporting: Selector<number> = (state) => state.dashboard.exporting;

export const getSelectedWidgets: Selector<string[]> = (state) => state.dashboard.selectedWidgets;

export const getActiveDashboardConfig: Selector<Dashboard | null> = (state) => {
  if (
    state.dashboard.activeUuid &&
    state.auth.user?.customer &&
    state.auth.user?.customer?.dashboards?.[state.dashboard.activeUuid]
  ) {
    return state.auth.user?.customer?.dashboards[state.dashboard.activeUuid];
  }
  return null;
};

// Dashboard filters tree

export const getNavParamsFromDashboardTree: Selector<RouteParams> = createSelector(
  [(state: GlobalState): string | string[] => state.nav.params.metrics__filters_search],
  (filtersSearch) => {
    if (typeof filtersSearch === 'string') {
      return navParamsFromTree(filtersSearch);
    }
    return {};
  }
);

export const getTree: Selector<Tree> = (state) => state.dashboard.filtersTree;

export const getFiltersSearchString: Selector<string> = (state) => {
  const metricParams = getNavParamsByResource(resourceQueryParamName.metrics)(state);
  const actualTree = getTree(state);

  let filtersSearch = metricParams.filters_search || '';

  if ((metricParams.created_after || metricParams.created_before) && !metricParams.days_from_oor) {
    const fs = addDatesToFiltersSearch(metricParams, actualTree);
    if (fs) {
      filtersSearch = fs;
    }
  }
  return filtersSearch as string;
};

export const getSelectedFilters: Selector<ValueNode[]> = (state) => state.dashboard.selectedFilters;
export const getSelectedFields: Selector<DataNode[]> = (state) => state.dashboard.selectedFields;

export const getRequestTree: Selector<boolean> = (state) => state.dashboard.requestTreeFilters;

export const getAllFilterValuesFromTreeWithLabels: Selector<TreeFieldsValuesWithLabels> =
  createSelector(
    [(state: GlobalState): GlobalState['envelopes']['filtersTree'] => state.dashboard.filtersTree],
    (filtersTree) => getAllFieldsValuesWithLabels(filtersTree)
  );

export const getFilterValuesFromTree: Selector<string[], [string]> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['filtersTree'] => state.dashboard.filtersTree,
    (_, field): string => field,
  ],
  (filtersTree, field) => lookAllForField(filtersTree, field)
);

export const makeGetFilterValuesFromTree = (): Selector<string[], [string]> =>
  createSelector(
    [
      (state: GlobalState): GlobalState['envelopes']['filtersTree'] => state.dashboard.filtersTree,
      (_, field): string => field,
    ],
    (filtersTree, field) => lookAllForField(filtersTree, field)
  );

export default { getCustomerDashboardConfig, getActiveUuid, getLoadedDashboards };
